import * as THREE from 'three';
import type { CameraAim, CameraDef, CameraKeyframe, EasingKey, Vec3 } from './types.ts';
import { shotAt, type CameraProgram } from './types.ts';

/**
 * Pure camera evaluation: keyframe interpolation + deterministic shake.
 * No state / viewport access — the director, viz and exporters all call this,
 * so preview and export stay frame-identical.
 */

export interface CameraSample {
  position: THREE.Vector3;
  target: THREE.Vector3;
  fov: number;
}

/** Resolves a non-path aim to a world point; null falls back to the keyframed target. */
export type AimResolver = (aim: CameraAim) => THREE.Vector3 | null;

// --- Easing ---------------------------------------------------------------------

/** Apply an easing curve to normalized segment progress s ∈ [0, 1]. */
export function applyEasing(easing: EasingKey, s: number): number {
  switch (easing) {
    case 'linear':
      return s;
    case 'ease':
      return s * s * (3 - 2 * s); // smoothstep in-out
    case 'ease-in':
      return s * s * s;
    case 'ease-out': {
      const inv = 1 - s;
      return 1 - inv * inv * inv;
    }
    case 'hold':
      return 0; // stay on this key until the next one
  }
}

// --- Deterministic noise (handheld shake) -----------------------------------------

/** Integer hash → [-1, 1]; pure so exports reproduce the preview exactly. */
function hashNoise(i: number, seed: number): number {
  let h = Math.imul(i ^ seed, 0x9e3779b1);
  h ^= h >>> 15;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  return ((h >>> 0) / 0xffffffff) * 2 - 1;
}

function valueNoise(seed: number, x: number): number {
  const i = Math.floor(x);
  const f = x - i;
  const s = f * f * (3 - 2 * f);
  return hashNoise(i, seed) * (1 - s) + hashNoise(i + 1, seed) * s;
}

/** Two-octave smooth value noise in [-1, 1], pure in (seed, t). */
export function noise1(seed: number, t: number): number {
  return valueNoise(seed, t) * 0.72 + valueNoise(seed ^ 0x5bd1e995, t * 2.17 + 13.7) * 0.28;
}

// --- Keyframe interpolation --------------------------------------------------------

/**
 * Segment lookup: returns [k0, k1, k2, k3] neighbourhood and eased local progress.
 * Times can be unevenly spaced — tangents account for real key spacing, so do NOT
 * replace this with a single CatmullRomCurve3 indexed by normalized u.
 */
function segmentAt(keys: CameraKeyframe[], t: number): { i: number; s: number } {
  if (t <= keys[0].time) return { i: 0, s: 0 };
  const last = keys.length - 1;
  if (t >= keys[last].time) return { i: Math.max(0, last - 1), s: 1 };
  let i = 0;
  while (i < last - 1 && keys[i + 1].time <= t) i++;
  const t1 = keys[i].time;
  const t2 = keys[i + 1].time;
  const s = t2 > t1 ? (t - t1) / (t2 - t1) : 1;
  return { i, s };
}

/** Non-uniform Catmull-Rom (Hermite) for one component. */
function hermite(p0: number, p1: number, p2: number, p3: number, t0: number, t1: number, t2: number, t3: number, s: number): number {
  const dt = t2 - t1;
  const m1 = dt * ((p2 - p0) / Math.max(1e-6, t2 - t0));
  const m2 = dt * ((p3 - p1) / Math.max(1e-6, t3 - t1));
  const s2 = s * s;
  const s3 = s2 * s;
  return (
    (2 * s3 - 3 * s2 + 1) * p1 + (s3 - 2 * s2 + s) * m1 + (-2 * s3 + 3 * s2) * p2 + (s3 - s2) * m2
  );
}

function interpVec3(
  keys: CameraKeyframe[],
  pick: (k: CameraKeyframe) => Vec3,
  smooth: boolean,
  i: number,
  s: number,
  out: THREE.Vector3
): THREE.Vector3 {
  const k1 = keys[i];
  const k2 = keys[Math.min(i + 1, keys.length - 1)];
  const a = pick(k1);
  const b = pick(k2);
  if (!smooth || keys.length < 3) {
    return out.set(
      a[0] + (b[0] - a[0]) * s,
      a[1] + (b[1] - a[1]) * s,
      a[2] + (b[2] - a[2]) * s
    );
  }
  const k0 = keys[Math.max(0, i - 1)];
  const k3 = keys[Math.min(keys.length - 1, i + 2)];
  const p0 = pick(k0);
  const p3 = pick(k3);
  // Virtual neighbours for the end segments keep tangents sane.
  const t0 = k0 === k1 ? k1.time - (k2.time - k1.time) : k0.time;
  const t3 = k3 === k2 ? k2.time + (k2.time - k1.time) : k3.time;
  return out.set(
    hermite(p0[0], a[0], b[0], p3[0], t0, k1.time, k2.time, t3, s),
    hermite(p0[1], a[1], b[1], p3[1], t0, k1.time, k2.time, t3, s),
    hermite(p0[2], a[2], b[2], p3[2], t0, k1.time, k2.time, t3, s)
  );
}

const DEG2RAD = Math.PI / 180;
const WORLD_UP = new THREE.Vector3(0, 1, 0);
const scratchFwd = new THREE.Vector3();
const scratchRight = new THREE.Vector3();
const scratchUp = new THREE.Vector3();

/**
 * Evaluate one camera at time t (seconds). Fills and returns `out` (allocated
 * when omitted). `resolveAim` overrides the look-at for non-path aim modes.
 */
export function evalCamera(
  def: CameraDef,
  t: number,
  resolveAim?: AimResolver,
  out?: CameraSample
): CameraSample {
  const sample: CameraSample = out ?? {
    position: new THREE.Vector3(),
    target: new THREE.Vector3(),
    fov: 45,
  };
  const keys = def.keyframes;
  const { i, s } = segmentAt(keys, t);
  const eased = applyEasing(keys[i].easing, s);
  const smooth = def.interp === 'smooth';

  interpVec3(keys, (k) => k.position, smooth, i, eased, sample.position);
  interpVec3(keys, (k) => k.target, smooth, i, eased, sample.target);
  const k2 = keys[Math.min(i + 1, keys.length - 1)];
  sample.fov = keys[i].fov + (k2.fov - keys[i].fov) * eased;

  // Tracking target overrides the keyframed look-at.
  if (def.aim.mode !== 'path' && resolveAim) {
    const aimed = resolveAim(def.aim);
    if (aimed) sample.target.copy(aimed);
  }

  // Handheld shake: deterministic drift added to position + aim.
  const shake = def.shake;
  if (shake?.enabled && (shake.posAmp > 0 || shake.rotAmp > 0)) {
    const nt = t * shake.freq;
    sample.position.x += noise1(shake.seed ^ 0x101, nt) * shake.posAmp;
    sample.position.y += noise1(shake.seed ^ 0x202, nt * 1.11) * shake.posAmp * 0.6;
    sample.position.z += noise1(shake.seed ^ 0x303, nt * 0.93) * shake.posAmp;

    const dist = sample.position.distanceTo(sample.target);
    const aimAmp = Math.tan(shake.rotAmp * DEG2RAD) * Math.max(0.1, dist);
    scratchFwd.subVectors(sample.target, sample.position).normalize();
    scratchRight.crossVectors(scratchFwd, WORLD_UP).normalize();
    if (scratchRight.lengthSq() < 1e-6) scratchRight.set(1, 0, 0);
    scratchUp.crossVectors(scratchRight, scratchFwd).normalize();
    sample.target.addScaledVector(scratchRight, noise1(shake.seed ^ 0x404, nt * 1.31) * aimAmp);
    sample.target.addScaledVector(scratchUp, noise1(shake.seed ^ 0x505, nt * 1.47) * aimAmp);
  }

  return sample;
}

/**
 * Evaluate the program (camera cuts) at time t. With no cuts, falls back to the
 * first camera; returns null when the program is empty.
 */
export function evalProgram(
  program: CameraProgram,
  t: number,
  resolveAim?: AimResolver,
  out?: CameraSample
): CameraSample | null {
  const shot = shotAt(program, t);
  const def = shot
    ? (program.cameras.find((c) => c.id === shot.cameraId) ?? program.cameras[0])
    : program.cameras[0];
  if (!def) return null;
  return evalCamera(def, t, resolveAim, out);
}
